import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';

import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';

import { AllowedIpFilterOptions } from '../models/allowed-ip-filter-option';
import { AllowedIpRegisterOptions } from '../models/allowed-ip-register-options';

import { DeleteAllowedIpVariables } from '../queries/generated/DeleteAllowedIp';

import { GetAllowedIpRegister, GetAllowedIpRegisterVariables } from '../queries/generated/GetAllowedIpRegister';

import { deleteAllowedIpMutation } from '../queries/deleteAllowedIpRegister-detail.graphql';
import { saveAllowedIpAddress, saveAllowedIpAddressVariables } from '../queries/generated/saveAllowedIpAddress';
import { getAllowedIpRegisterQuery } from '../queries/getAllowedIpRegister.graphql';
import { SaveAllowedIpMutation } from '../queries/saveAllowedIpRegister.graphql';

@Injectable({ providedIn: 'root' })
export class AllowedIpRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getAllowedIps(
      endCursor: string | null,
      filters: AllowedIpFilterOptions[] | null,
      quickFilters: AllowedIpRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetAllowedIpRegister>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertAllowedIpFilterOptions.bind(this)),
              }
            : { filterOptions: [] };

      if (quickFilters?.searchText) {
         const searchText = quickFilters.searchText.trim();

         const subExpression: FilterExpression = {
            filterOptions: [],
            logicalOperator: LogicalOperators.Or,
         };

         subExpression.filterOptions.push({
            field: 'IpAddress',
            operation: FilterOperations.Contains,
            value: searchText,
         });

         subExpression.filterOptions.push({
            field: 'QManageTicketId',
            operation: FilterOperations.Contains,
            value: searchText,
         });

         filterBuilderFilterExpression.subExpressions = [subExpression];
      }

      const variables = this.getAllowedIpsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);

      return this.apiService.query<GetAllowedIpRegister, GetAllowedIpRegisterVariables>({
         query: getAllowedIpRegisterQuery,
         variables,
      });
   }

   private convertAllowedIpFilterOptions(option: AllowedIpFilterOptions): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   changeAllowedIpData(
      allowedIpAddressId: number,
      qManageTicketId: string,
      accountId: number,
      fields: string[]
   ): Observable<ExecutionResult<saveAllowedIpAddress>> {
      return this.apiService.query<saveAllowedIpAddress, saveAllowedIpAddressVariables>({
         query: SaveAllowedIpMutation,
         variables: {
            allowedIpAddress: {
               allowedIpAddressId,
               qManageTicketId,
               accountId,
               fields,
            },
         },
      });
   }

   private getFilterOptionValue(option: AllowedIpFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }

      switch (option.type) {
         case 'allowedIp':
            return Array.isArray(option.value)
               ? option.value.map((v) => v.allowedIpAddressId)
               : option.value?.allowedIpAddressId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }

      return null;
   }

   private getAllowedIpsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetAllowedIpRegisterVariables {
      return {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      }

      return '';
   }

   deleteAllowedIp(allowedIpAddressId: number): Observable<ExecutionResult<void>> {
      return this.apiService.query<void, DeleteAllowedIpVariables>({
         query: deleteAllowedIpMutation,
         variables: {
            allowedIpAddressId,
         },
      });
   }
}
